/**
 * bankMatching/matchIncoming — Eingangs-Matching (BR-4).
 *
 * Ordnet eingehende Gutschriften (`amountCents > 0`) offenen Rechnungen zu.
 * Pure, deterministisch, erklärbar (§8.3/§8.4/§8.5, kanonische Matrix §8.10).
 *
 * GELD-DISZIPLIN & Sicherheits-Guards (v2):
 *  - Regel 4 (§8.4): `AMOUNT_SKONTO` erzwingt IMMER `suggest`, NIE `auto` —
 *    ein Auto-Match würde via `computeOpenItemStatus` die Skonto-Restdifferenz
 *    als `paid` werten und eine LIVE-M2-Mahnung still unterdrücken.
 *  - Regel 5 (§8.4): bei `hasActiveDunning` ist `auto` nur bei `NUMBER_EXACT`
 *    UND exakt offenem Betrag erlaubt; sonst `suggest` + `DUNNING_GUARD`.
 *  - Σ-Guard: dieselbe Rechnung darf nicht von zwei Txs gleichzeitig `auto`
 *    beansprucht werden, wenn Σ > open + Skonto-Toleranz (Txs nach bookingDate
 *    ASC verarbeitet → deterministisch).
 *
 * Determinismus: Kandidaten-Reihenfolge unabhängig von Eingabe-Reihenfolge —
 * Txs werden nach (bookingDate ASC, txId ASC) verarbeitet, das Ausgabe-Array
 * final nach (score DESC, txId ASC, targetId ASC) sortiert.
 *
 * VORFILTER-VERTRAG (§8.3): Als interne Umbuchung erkannte Txs (Vorstufe 1,
 * `detectInternalTransfers`) sind auf der Eingangsseite auszuschließen. Die
 * Signatur `matchIncomingTransactions(txs, invoices, todayISO)` trägt KEIN
 * Exclusion-Set — der Aufrufer (BR-6) filtert diese Txs VOR dem Aufruf heraus.
 * Fremdwährungs-Txs werden hier defensiv gefiltert (Belegtest M9).
 *
 * @module bankMatching/matchIncoming
 */

import { BankTransaction } from '../../types/bankTypes';
import {
  MatchableInvoice,
  MatchSuggestion,
  MatchReason,
  MATCH_REASON,
  MIN_SUGGESTION_SCORE,
  AUTO_MIN_SCORE,
  AUTO_UNIQUENESS_GAP,
} from './types';
import {
  normalizeRef,
  refContains,
  nameSimilarity,
  addDaysToISO,
} from './normalize';

/** Score-Konstanten (§8.3). */
const SCORE = {
  NUMBER_EXACT: 50,
  AMOUNT_OPEN_EXACT: 30,
  AMOUNT_TOTAL_EXACT: 25,
  AMOUNT_SKONTO: 25,
  AMOUNT_PARTIAL: 8,
  NAME_STRONG: 15,
  NAME_WEAK: 8,
  DATE_PLAUSIBLE: 5,
  DATE_BEFORE_DOC: -20,
} as const;

const NAME_STRONG_THRESHOLD = 0.8;
const NAME_WEAK_THRESHOLD = 0.5;

/** Interner Score-Datensatz eines (tx, invoice)-Paars. */
interface ScoredCandidate {
  invoice: MatchableInvoice;
  score: number;
  reasons: MatchReason[];
  skontoDetected: boolean;
  /** `tx > open` UND `NUMBER_EXACT` → Überzahlungsbetrag (sonst undefined). */
  overpaymentCents?: number;
}

/**
 * Skonto-Toleranz einer Rechnung — identisch zu `openItemStatus.ts:163-166`
 * (`Math.round(total × pct / 100)`; 0 ohne Skonto).
 */
function skontoToleranceCents(inv: MatchableInvoice): number {
  if (inv.skontoPercentage == null) return 0;
  return Math.round((inv.storedGrandTotalCents * inv.skontoPercentage) / 100);
}

/**
 * Bewertet ein einzelnes (tx, invoice)-Paar gemäß §8.3.
 * Betrags-Signale sind exklusiv (max. eines zählt); Namens-Signale exklusiv;
 * Datums-Signale exklusiv.
 */
function scorePair(
  tx: BankTransaction,
  inv: MatchableInvoice,
): ScoredCandidate {
  const reasons: MatchReason[] = [];
  let score = 0;
  let skontoDetected = false;
  let overpaymentCents: number | undefined;

  const amount = tx.amountCents;
  const open = inv.openAmountCents;

  // ── NUMBER_EXACT ──────────────────────────────────────────────────────
  const numberHit =
    refContains(normalizeRef(tx.purpose), normalizeRef(inv.invoiceNumber));
  if (numberHit) {
    reasons.push({
      code: MATCH_REASON.NUMBER_EXACT,
      detail: `${inv.invoiceNumber} im Verwendungszweck`,
      scoreDelta: SCORE.NUMBER_EXACT,
    });
    score += SCORE.NUMBER_EXACT;
  }

  // ── Betrags-Signal (exklusiv, max. eines) ─────────────────────────────
  const tol = skontoToleranceCents(inv);
  const skontoDeadlineISO =
    inv.skontoPercentage != null
      ? addDaysToISO(inv.invoiceDateISO, inv.skontoDays ?? 0)
      : null;
  const withinSkontoWindow =
    skontoDeadlineISO != null && tx.bookingDate <= skontoDeadlineISO;

  if (amount === open) {
    reasons.push({
      code: MATCH_REASON.AMOUNT_OPEN_EXACT,
      detail: 'Betrag == offener Betrag',
      scoreDelta: SCORE.AMOUNT_OPEN_EXACT,
    });
    score += SCORE.AMOUNT_OPEN_EXACT;
  } else if (amount === inv.storedGrandTotalCents) {
    // tx == Gesamtbetrag, aber ≠ open (Teilzahlung existierte schon).
    reasons.push({
      code: MATCH_REASON.AMOUNT_TOTAL_EXACT,
      detail: 'Betrag == Rechnungssumme (Teilzahlung existiert)',
      scoreDelta: SCORE.AMOUNT_TOTAL_EXACT,
    });
    score += SCORE.AMOUNT_TOTAL_EXACT;
  } else if (
    inv.skontoPercentage != null &&
    open - amount > 0 &&
    open - amount <= tol &&
    withinSkontoWindow
  ) {
    reasons.push({
      code: MATCH_REASON.AMOUNT_SKONTO,
      detail: `Skontoabzug ${inv.skontoPercentage}% erkannt`,
      scoreDelta: SCORE.AMOUNT_SKONTO,
    });
    score += SCORE.AMOUNT_SKONTO;
    skontoDetected = true;
  } else if (amount > 0 && amount < open) {
    reasons.push({
      code: MATCH_REASON.AMOUNT_PARTIAL,
      detail: 'Teilzahlung (Betrag < offener Betrag)',
      scoreDelta: SCORE.AMOUNT_PARTIAL,
    });
    score += SCORE.AMOUNT_PARTIAL;
  }

  // Überzahlung: tx > open UND klarer Nummern-Bezug (§8.3 Soll-Paar 6 / M3).
  if (amount > open && numberHit) {
    overpaymentCents = amount - open;
  }

  // ── Namens-Signal (exklusiv) ──────────────────────────────────────────
  const sim = nameSimilarity(tx.counterpartyName, inv.buyerName);
  if (sim >= NAME_STRONG_THRESHOLD) {
    reasons.push({
      code: MATCH_REASON.NAME_STRONG,
      detail: 'Zahler-Name entspricht Käufer (stark)',
      scoreDelta: SCORE.NAME_STRONG,
    });
    score += SCORE.NAME_STRONG;
  } else if (sim >= NAME_WEAK_THRESHOLD) {
    reasons.push({
      code: MATCH_REASON.NAME_WEAK,
      detail: 'Zahler-Name ähnelt Käufer (schwach)',
      scoreDelta: SCORE.NAME_WEAK,
    });
    score += SCORE.NAME_WEAK;
  }

  // ── Datums-Signal (exklusiv) ──────────────────────────────────────────
  if (tx.bookingDate < inv.invoiceDateISO) {
    reasons.push({
      code: MATCH_REASON.DATE_BEFORE_DOC,
      detail: 'Buchung vor Rechnungsdatum',
      scoreDelta: SCORE.DATE_BEFORE_DOC,
    });
    score += SCORE.DATE_BEFORE_DOC;
  } else {
    const upperBase = inv.dueDateISO ?? addDaysToISO(inv.invoiceDateISO, 30);
    const upper = addDaysToISO(upperBase, 60);
    if (tx.bookingDate >= inv.invoiceDateISO && tx.bookingDate <= upper) {
      reasons.push({
        code: MATCH_REASON.DATE_PLAUSIBLE,
        detail: 'Buchungsdatum im plausiblen Zahlungsfenster',
        scoreDelta: SCORE.DATE_PLAUSIBLE,
      });
      score += SCORE.DATE_PLAUSIBLE;
    }
  }

  return { invoice: inv, score, reasons, skontoDetected, overpaymentCents };
}

/** Prüft, ob ein Kandidat den Reason `NUMBER_EXACT` trägt. */
function hasNumberExact(c: ScoredCandidate): boolean {
  return c.reasons.some((r) => r.code === MATCH_REASON.NUMBER_EXACT);
}

/** Prüft, ob ein Kandidat `AMOUNT_OPEN_EXACT` UND `NAME_STRONG` trägt. */
function hasOpenExactAndStrongName(c: ScoredCandidate): boolean {
  const codes = new Set(c.reasons.map((r) => r.code));
  return (
    codes.has(MATCH_REASON.AMOUNT_OPEN_EXACT) &&
    codes.has(MATCH_REASON.NAME_STRONG)
  );
}

/**
 * Versucht, eine Sammelüberweisung als Split zu erkennen (§8.5).
 * Bedingung: ≥2 offene Rechnungsnummern im Verwendungszweck UND
 * Σ Abrechnungsbetrag === `tx.amountCents` (exakt offen ODER exakt
 * skonto-reduziert je Rechnung, wenn deren Skonto-Fenster die Buchung deckt).
 *
 * @returns Split-`MatchSuggestion` oder `null` (dann normales Matching).
 */
function tryDetectSplit(
  tx: BankTransaction,
  candidates: MatchableInvoice[],
): MatchSuggestion | null {
  const purposeNorm = normalizeRef(tx.purpose);
  if (!purposeNorm) return null;

  const hits = candidates.filter((inv) =>
    refContains(purposeNorm, normalizeRef(inv.invoiceNumber)),
  );
  if (hits.length < 2) return null;

  // Variante A: exakte Summe der offenen Beträge.
  const openSum = hits.reduce((acc, inv) => acc + inv.openAmountCents, 0);
  if (openSum === tx.amountCents) {
    return buildSplitSuggestion(
      tx,
      hits.map((inv) => ({ invoiceId: inv.invoiceId, amountCents: inv.openAmountCents })),
      false,
    );
  }

  // Variante B: je Rechnung skonto-reduziert, wenn das Skonto-Fenster
  // die Buchung deckt (deterministisch: ein einziger Kandidatenbetrag pro Rechnung).
  const reducedParts = hits.map((inv) => {
    const tol =
      inv.skontoPercentage == null
        ? 0
        : Math.round((inv.storedGrandTotalCents * inv.skontoPercentage) / 100);
    const deadlineISO =
      inv.skontoPercentage != null
        ? addDaysToISO(inv.invoiceDateISO, inv.skontoDays ?? 0)
        : null;
    const withinWindow = deadlineISO != null && tx.bookingDate <= deadlineISO;
    const amountCents =
      withinWindow && tol > 0 ? inv.openAmountCents - tol : inv.openAmountCents;
    return { invoiceId: inv.invoiceId, amountCents };
  });
  const reducedSum = reducedParts.reduce((acc, p) => acc + p.amountCents, 0);
  if (reducedSum === tx.amountCents && reducedSum !== openSum) {
    return buildSplitSuggestion(tx, reducedParts, true);
  }

  return null;
}

/** Baut das `split-invoices`-`MatchSuggestion` (Σ == tx.amountCents, Invariante). */
function buildSplitSuggestion(
  tx: BankTransaction,
  parts: Array<{ invoiceId: string; amountCents: number }>,
  skontoDetected: boolean,
): MatchSuggestion {
  const reasons: MatchReason[] = [
    {
      code: MATCH_REASON.NUMBER_EXACT,
      detail: `${parts.length} Rechnungsnummern im Verwendungszweck`,
      scoreDelta: SCORE.NUMBER_EXACT,
    },
    {
      code: MATCH_REASON.AMOUNT_OPEN_EXACT,
      detail: 'Summe der Teilbeträge == Überweisungsbetrag',
      scoreDelta: SCORE.AMOUNT_OPEN_EXACT,
    },
  ];
  return {
    txId: tx.id,
    kind: 'split-invoices',
    targetIds: parts.map((p) => p.invoiceId),
    // Split ist immer menschlich zu bestätigen (Mehrfach-Zuordnung, Geld) → suggest.
    score: SCORE.NUMBER_EXACT + SCORE.AMOUNT_OPEN_EXACT,
    confidence: 'suggest',
    reasons,
    skontoDetected,
    splitParts: parts,
  };
}

/**
 * Eingangs-Matching (§8.3/§8.4/§8.5). Ordnet Gutschriften offenen Rechnungen zu.
 *
 * @param txs - Kontoumsätze (nur `amountCents > 0`, EUR werden verarbeitet;
 *   der Aufrufer schließt erkannte interne Umbuchungen vorab aus, §8.3).
 * @param invoices - offene Rechnungs-Kandidaten (Mapper liefert; `openAmountCents > 0`
 *   wird hier erzwungen).
 * @param todayISO - heutiges Datum `YYYY-MM-DD` (Reinheit; aktuell nicht
 *   scoring-relevant, für Signatur-Konsistenz und künftige Fälligkeits-Logik).
 * @returns Deterministisch sortiertes Array von `MatchSuggestion`.
 */
export function matchIncomingTransactions(
  txs: BankTransaction[],
  invoices: MatchableInvoice[],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  todayISO: string,
): MatchSuggestion[] {
  // Kandidatenraum: nur offene Rechnungen (openAmountCents > 0).
  const openInvoices = invoices.filter((inv) => inv.openAmountCents > 0);

  // Nur EUR-Gutschriften; deterministische Verarbeitungsreihenfolge.
  // Ausschluss NUR bei explizit gesetzter Fremdwährung (§8.3 „currency !== 'EUR'");
  // fehlende/leere currency wird als inländisch (EUR) behandelt.
  const creditTxs = txs
    .filter((tx) => tx.amountCents > 0 && !(tx.currency && tx.currency !== 'EUR'))
    .slice()
    .sort((a, b) => {
      if (a.bookingDate !== b.bookingDate) {
        return a.bookingDate < b.bookingDate ? -1 : 1;
      }
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });

  const suggestions: MatchSuggestion[] = [];
  // Σ-Guard: pro Rechnung die Summe bereits AUTO-beanspruchter tx-Beträge.
  const autoClaimedByInvoice = new Map<string, number>();

  for (const tx of creditTxs) {
    // Split zuerst — konsumiert die tx vollständig (kein Einzel-Matching).
    const split = tryDetectSplit(tx, openInvoices);
    if (split) {
      suggestions.push(split);
      continue;
    }

    // Alle Kandidaten bewerten.
    const scored = openInvoices.map((inv) => scorePair(tx, inv));
    if (scored.length === 0) continue;

    // Best/Zweitbester über ALLE Kandidaten (vor Floor) für die Gap-Regel.
    const byScoreDesc = scored
      .slice()
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return a.invoice.invoiceId < b.invoice.invoiceId ? -1 : 1;
      });
    const best = byScoreDesc[0];
    const secondScore = byScoreDesc.length > 1 ? byScoreDesc[1].score : 0;

    // Auto-Entscheidung nur für den Best-Kandidaten (§8.4).
    let bestIsAuto = false;
    if (best.score >= AUTO_MIN_SCORE) {
      const gapOk = best.score - secondScore >= AUTO_UNIQUENESS_GAP;
      const signalOk = hasNumberExact(best) || hasOpenExactAndStrongName(best);
      const skontoBlocks = best.skontoDetected; // Regel 4
      let dunningBlocks = false;
      if (best.invoice.hasActiveDunning) {
        // Regel 5: auto nur bei NUMBER_EXACT UND exaktem offenen Betrag.
        const dunningOk =
          hasNumberExact(best) && tx.amountCents === best.invoice.openAmountCents;
        dunningBlocks = !dunningOk;
      }

      if (gapOk && signalOk && !skontoBlocks && !dunningBlocks) {
        // Σ-Guard: Zweit-Anspruch auf dieselbe Rechnung, der Σ > open + Toleranz
        // triebe, degradiert zu suggest.
        const already = autoClaimedByInvoice.get(best.invoice.invoiceId) ?? 0;
        const limit = best.invoice.openAmountCents + skontoToleranceCents(best.invoice);
        if (already + tx.amountCents > limit) {
          bestIsAuto = false;
        } else {
          bestIsAuto = true;
          autoClaimedByInvoice.set(best.invoice.invoiceId, already + tx.amountCents);
        }
      } else if (best.invoice.hasActiveDunning && gapOk && signalOk && !skontoBlocks && dunningBlocks) {
        // Der Dunning-Guard hat einen sonst auto-fähigen Treffer degradiert →
        // erklärender Reason (Δ0) am Best-Kandidaten (§8.4 / M10b).
        best.reasons.push({
          code: MATCH_REASON.DUNNING_GUARD,
          detail: `stoppt Mahnstufe ${best.invoice.dunningLevel ?? '?'} zu ${best.invoice.invoiceNumber}`,
          scoreDelta: 0,
        });
      }
    }

    // Emission: alle Kandidaten mit score ≥ Floor; nur der Best-Kandidat kann auto sein.
    for (const cand of scored) {
      if (cand.score < MIN_SUGGESTION_SCORE) continue;
      const isBest = cand === best;
      const suggestion: MatchSuggestion = {
        txId: tx.id,
        kind: 'invoice',
        targetIds: [cand.invoice.invoiceId],
        score: cand.score,
        confidence: isBest && bestIsAuto ? 'auto' : 'suggest',
        reasons: cand.reasons,
        skontoDetected: cand.skontoDetected,
      };
      if (cand.overpaymentCents != null) {
        suggestion.overpaymentCents = cand.overpaymentCents;
      }
      suggestions.push(suggestion);
    }
  }

  return sortSuggestions(suggestions);
}

/**
 * Finale, deterministische Sortierung des Ausgabe-Arrays:
 * score DESC, dann txId ASC, dann erstes targetId ASC, dann kind ASC.
 */
export function sortSuggestions(list: MatchSuggestion[]): MatchSuggestion[] {
  return list.slice().sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    if (a.txId !== b.txId) return a.txId < b.txId ? -1 : 1;
    const at = a.targetIds[0] ?? '';
    const bt = b.targetIds[0] ?? '';
    if (at !== bt) return at < bt ? -1 : 1;
    if (a.kind !== b.kind) return a.kind < b.kind ? -1 : 1;
    return 0;
  });
}
